import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import {
  createContext,
  useContext,
  useMemo,
} from "react";
import { useGeotabClient } from "./GeotabClientContext";
import { PageType, viewSelectionKey } from "@/constants/PageType";
import { useRememberedSelection } from "@/hooks/userState/useRememberedSelection";
import { mergeGridState } from "@/hooks/views/mergeGridState";
import { useDiagnosticViews } from "@/hooks/views/useDiagnosticViews";
import { useViewMigration } from "@/hooks/views/useViewMigration";
import { useViewOverride } from "@/hooks/views/useViewOverride";
import { useVisibleViews } from "@/hooks/views/useVisibleViews";
import { DiagnosticView, ViewOverride, ViewPageType } from "@/models/DiagnosticView";

interface ViewContextValue {
  views: DiagnosticView[];
  visibleViews: DiagnosticView[];
  selectedView: DiagnosticView | null;
  setSelectedView: (next: DiagnosticView | null) => void;
  mergedGridState: GridInitialStatePro;
  override: ViewOverride | null;
  hasOverride: boolean;
  isFairfleetAdmin: boolean;
  pageType: PageType;
}

const ViewContext = createContext<ViewContextValue | undefined>(undefined);

const FORCE_VIEW_MODE_KEY = "forceViewMode";

export function isViewModeActive(views: DiagnosticView[]): boolean {
  if (views.length > 0) return true;
  if (sessionStorage.getItem("diagViewsMigrating") === "1") return true;
  if (localStorage.getItem(FORCE_VIEW_MODE_KEY) === "true") return true;
  return false;
}

export function ViewProvider({
  children,
  pageType,
}: {
  children: React.ReactNode;
  pageType: PageType;
}) {
  useViewMigration();
  const { data: views = [] } = useDiagnosticViews();
  const visibleViews = useVisibleViews(
    views.filter(v => v.pageTypes.includes(pageType as ViewPageType)),
  );
  const { username } = useGeotabClient();
  const isFairfleetAdmin = username?.endsWith("@fairfleet.de") ?? false;

  // Was one unscoped browser key for every user and every page — the same defect the preset
  // selection had. Now remembered per user and per page.
  const { selected: selectedView, setSelected: setSelectedView } = useRememberedSelection(
    viewSelectionKey(pageType),
    visibleViews,
  );

  const { override } = useViewOverride(pageType, selectedView?.id ?? null);

  const mergedGridState = useMemo(
    () => (selectedView ? mergeGridState(selectedView, override) : {}),
    [selectedView, override],
  );

  const value: ViewContextValue = {
    views,
    visibleViews,
    selectedView,
    setSelectedView,
    mergedGridState,
    override,
    hasOverride: !!override,
    isFairfleetAdmin,
    pageType,
  };

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
}

export function useViewContext(): ViewContextValue {
  const ctx = useContext(ViewContext);
  if (!ctx) throw new Error("useViewContext must be used within a ViewProvider");
  return ctx;
}

export function useOptionalViewContext(): ViewContextValue | null {
  return useContext(ViewContext) ?? null;
}
